/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import {
  ColumnDef,
  PaginationState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useMemo } from 'react';
import {
  Alert,
  Card,
  Col,
  Form,
  Pagination,
  Row,
  Stack,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Invoice } from 'types';
import { paths } from 'app/paths';
import { formatDate } from 'app/utils/formatDate';
import { formatCurrency } from 'app/utils/formatCurrency';

interface Props {
  invoices: Invoice[];
  totalEntries: number;
  pagination: PaginationState;
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const TODAY = new Date();

const PaidBadge = styled.span`
  background-color: #fff3cd;
  color: #856404;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
`;

const NotPaidBadge = styled.span`
  background-color: #d1f2e9;
  color: #0a6d52;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
`;

const WarningBadge = styled.span`
  background-color: #f3003f;
  color: #f3faff;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
`;

const TableContainer = styled.div`
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #dee2e6;
`;
const StyledTableRow = styled.tr<{ status: 'paid' | 'due' | 'past-due' }>`
  background-color: ${({ status }) =>
    status === 'paid'
      ? '#ffefff'
      : status === 'past-due'
      ? '#fef1f0'
      : '#fffbea'};
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${({ status }) =>
      status === 'paid'
        ? '#fde0de'
        : status === 'past-due'
        ? '#d0eee6'
        : '#fff4c2'};
  }

  // Remove default bootstrap hover background
  & > td {
    background-color: transparent !important;
  }
`;

const InvoiceTableCard = ({
  invoices,
  totalEntries,
  pagination,
  setPagination,
}: Props) => {
  const columns = useMemo<ColumnDef<Invoice>[]>(
    () => [
      {
        header: 'Id',
        accessorKey: 'id',
        cell: ({ row }) => (
          <Link to={paths.invoices.show(row.original.id)} className="px-2">
            {row.original.id}
          </Link>
        ),
      },
      {
        header: 'Customer',
        accessorKey: 'customer',
        accessorFn: (row) => {
          const { first_name, last_name } = row.customer || {};
          return first_name && last_name ? `${first_name} ${last_name}` : 'N/A';
        },
      },
      {
        header: 'Total',
        accessorKey: 'total',
        cell: ({ row }) => <span>{formatCurrency(row.original.total)}</span>,
      },
      {
        header: 'Tax',
        accessorKey: 'tax',
        cell: ({ row }) => <span>{formatCurrency(row.original.tax)}</span>,
      },
      {
        header: 'Finalized',
        accessorKey: 'finalized',
        cell: ({ row }) => (row.original.finalized ? 'Yes' : 'No'),
      },
      {
        header: 'Date',
        accessorKey: 'date',
        cell: ({ row }) =>
          row.original.date ? formatDate(row.original.date) : 'N/A',
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => {
          const { paid, deadline } = row.original;
          if (!deadline) return null;

          const deadlineDate = new Date(deadline);
          const isPastDue = deadlineDate < TODAY;

          if (paid) return <PaidBadge>Paid</PaidBadge>;
          return isPastDue ? (
            <NotPaidBadge>Past due {formatDate(deadline)}</NotPaidBadge>
          ) : (
            <WarningBadge>Due {formatDate(deadline)}</WarningBadge>
          );
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data: invoices,
    columns,
    rowCount: totalEntries,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();
  const firstEntry = pageIndex * pageSize + 1;
  const lastEntry = Math.min((pageIndex + 1) * pageSize, totalEntries);

  if (invoices.length === 0) {
    return (
      <Alert variant="info" className="m-3">
        No invoices found matching your criteria.
      </Alert>
    );
  }

  return (
    <>
      <TableContainer className="table-responsive">
        <table className="table table-hover mb-0">
          <thead className="bg-light">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="py-3">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              const { paid, deadline } = row.original;
              const deadlineDate = deadline ? new Date(deadline) : null;
              const isPastDue = deadlineDate ? deadlineDate < TODAY : false;

              let status: 'paid' | 'due' | 'past-due' = 'due';
              if (paid) status = 'paid';
              else if (isPastDue) status = 'past-due';

              return (
                <StyledTableRow key={row.id} status={status}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="py-3 align-middle">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </StyledTableRow>
              );
            })}
          </tbody>
        </table>
      </TableContainer>

      <Card.Footer className="bg-white border-0 px-3">
        <Row className="align-items-center">
          <Col>
            <div className="text-muted small">
              Showing {firstEntry} to {lastEntry} of {totalEntries} invoices
            </div>
          </Col>
          <Col className="d-flex justify-content-end">
            <Stack direction="horizontal" gap={2}>
              <Pagination className="mb-0">
                <Pagination.First
                  onClick={() => table.firstPage()}
                  disabled={!table.getCanPreviousPage()}
                />
                <Pagination.Prev
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                />
                {Array.from({ length: 3 }, (_, i) => {
                  const start = Math.max(
                    0,
                    Math.min(pageIndex - 1, pageCount - 3)
                  );
                  const pageNum = start + i;
                  if (pageNum >= pageCount) return null;
                  return (
                    <Pagination.Item
                      key={pageNum}
                      active={pageNum === pageIndex}
                      onClick={() => table.setPageIndex(pageNum)}
                    >
                      {pageNum + 1}
                    </Pagination.Item>
                  );
                })}
                <Pagination.Next
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                />
                <Pagination.Last
                  onClick={() => table.lastPage()}
                  disabled={!table.getCanNextPage()}
                />
              </Pagination>
              <Form.Select
                size="sm"
                style={{ width: '80px' }}
                value={pageSize}
                onChange={(e) => table.setPageSize(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Form.Select>
            </Stack>
          </Col>
        </Row>
      </Card.Footer>
    </>
  );
};

export default InvoiceTableCard;
